/* eslint-disable no-use-before-define */
/* eslint-disable no-param-reassign */
import last from 'lodash/last';
import extend from 'lodash/extend';
import find from 'lodash/find';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import map from 'lodash/map';
import forEach from 'lodash/forEach';
import uuid from 'uuid/v4';
import { eachHierarchy } from '@Hooks/LibraryInitialization/MoreUnderscore';
import * as StructureContainers from '@Services/StructureContainers';

const FIELD_ID = {
  ECT: 'ECT',
  PHARM: 'PHARM',
};

class QueryParser {
  _removeEmptyOperators = true;

  _logicOperatorsMap;

  stringifyQuery(queryStructureArray) {
    return this._jsonComposer(queryStructureArray);
  }

  parseQuery(semanticQuery, properties, logicOperatorsMap) {
    return this._nodeListComposer(semanticQuery, properties, logicOperatorsMap);
  }

  setRemoveEmptyOperators(emptyOperators) {
    this._removeEmptyOperators = emptyOperators;
  }

  setLogicOperatorsMap(operators) {
    this._logicOperatorsMap = operators;
  }

  _jsonComposer(nodesList) {
    const returnObj = {
      id: 'root',
      facts: [],
    };
    if (nodesList && nodesList.length > 0) {
      returnObj.facts = returnObj.facts.concat(this._recursiveMapper(nodesList));
    }
    return returnObj;
  }

  _recursiveMapper(facts) {
    return map(facts, (node, index) => {
      let returnObj;

      if (node.isShell && node.items && node.items.length > 0) {
        returnObj = {
          id: node.id,
          groupName: node.groupName,
          displayName: node.displayName,
          exist: !!node.exist,
          facts: this._recursiveMapper(node.items),
        };
      } else if (node.type === 'history') {
        returnObj = {
          exist: false,
          historyItems: [
            {
              resultName: node.resultName,
              databaseId: node.databaseId,
            },
          ],
        };
      } else if (node.type === 'structure') {
        returnObj = this._createStructureObject(node);
      } else if (node.type === 'advanced') {
        returnObj = {
          id: node.id,
          exist: !!node.exist,
          advancedItems: [{ advancedExpression: node.value, databaseId: node.databaseId }],
        };
      } else {
        returnObj = {
          id: node.id,
          exist: !!node.exist,
        };

        this._recursiveMapperRemoveEmptyOperator(node, returnObj);
      }

      if (index > 0) {
        returnObj.logicOperator = node.operator;
        if (node.operatorQualifier) {
          returnObj.logicOperatorQualifier = node.operatorQualifier;
        }
      }
      return returnObj;
    });
  }

  _recursiveMapperRemoveEmptyOperator(node, returnObj) {
    if (this._removeEmptyOperators) {
      if (!node.exist && node.children && node.children.length > 0) {
        this._setFieldsProps(returnObj, node);
      }
    } else {
      this._setFieldsProps(returnObj, node);
    }
  }

  _setFieldsProps(returnObj, node) {
    const { children } = node;
    if (children) {
      const lastChild = last(children); // since the first child should not have operator
      returnObj.fieldsLogicOperator = lastChild.operator || lastChild.defaultOperation || node.defaultOperation;
      if (lastChild.operatorQualifier) {
        returnObj.fieldsLogicOperatorQualifier = lastChild.operatorQualifier;
      }
      returnObj.fields = this._parseChildren(children);
    }
  }

  _parseChildren(children) {
    return map(children, (child) => this._getChildValues(child));
  }

  _getChildValues(child) {
    const field = {
      validation: child.validation || [],
    };
    if (!this._removeEmptyOperators || child.value) {
      extend(field, {
        id: child.id,
        value: child.value,
        boundOperator: child.operation || child.defaultBoundOperator,
        displayName: child.displayName,
        dataType: child.dataType,
      });
    }
    return field;
  }

  _createStructureObject(structure) {
    const result = {
      id: 'wrapper',
      exist: !!structure.exist,
      structures: [],
      facts: [],
    };
    if (structure.items && structure.items.length > 0) {
      result.structures = structure.items.filter((item) => item.type === 'structure').map((item) => this._getStructureProps(item));
      result.facts = structure.items.filter((item) => item.type === 'fact');
    } else {
      result.structures = [this._getStructureProps(structure)];
    }
    if (result.facts.length > 0) {
      // operator with nested Structure QL
      const { operator } = result.facts[0];
      const { operatorQualifier } = result.facts[0];
      result.facts = this._recursiveMapper(result.facts);
      result.facts[0].logicOperator = operator;
      if (operatorQualifier) {
        result.facts[0].logicOperatorQualifier = operatorQualifier;
      }
    }
    return result;
  }

  _getStructureProps(node) {
    // export and search
    const props = {
      structure: node.structureData,
      options: node.options || [],
      id: node.id,
    };
    if (node.structureName) {
      props.structureName = node.structureName;
    }
    return props;
  }

  _nodeListComposer(semanticQuery, properties) {
    const result = [];
    const createPredefinedItem = (queryItem) => ({
      id: queryItem.id,
      items: [],
      isShell: true,
      isImmutable: queryItem.isImmutable,
      exist: queryItem.exist,
      operator: queryItem.logicOperator || 'AND',
      groupName: queryItem.groupName || 'Group',
      displayName: queryItem.displayName,
      uuid: uuid(),
    });

    const handleStructureQuery = (queryItem, item, parent) => {
      forEach(queryItem.structures, (structure) => processChildren(structure, (item && item.items) || parent));
    };

    const handleFactQuery = (queryItem, item, parent) => {
      forEach(queryItem.facts, (fact) => processChildren(fact, (item && item.items) || parent));
    };

    // eslint-disable-next-line consistent-return
    const processChildren = (queryItem, parent) => {
      // RXFE-7999 - set defaultQualifier
      const qualifier = queryItem.logicOperatorQualifier || this._getDefaultQualifier(createPredefinedItem(queryItem).operator);
      if (qualifier) {
        createPredefinedItem(queryItem).operatorQualifier = qualifier;
      }

      let item;

      // RXFE-4753: all structure qls should have 'wrapper' before sending to BE,
      // but it should be removed when adding forms
      if (queryItem.structures && queryItem.id && queryItem.id === 'wrapper') {
        // remove wrapper of structures
        delete queryItem.id;
      }
      if (queryItem.id && queryItem.id === 'wrapper') {
        item = createPredefinedItem(queryItem);
        parent.push(item);
      } else if (queryItem.id && queryItem.id !== 'root') {
        // root is not needed for the VIEW, otherwise create a Property Node if ID exist
        item = this._createFactProperty(queryItem, properties);
        // parent is a reference to result[n].items with corresponding items per level of nesting (items[0].items[0]..)
        if (item) {
          parent.push(item);
          this._setFieldsValues(item, queryItem);
        } else {
          return null;
        }
      }

      if (queryItem.logicOperator && queryItem.structures) {
        queryItem.structures[0].logicOperator = queryItem.logicOperator;
      }
      if (queryItem.logicOperatorQualifier && queryItem.structures) {
        queryItem.structures[0].logicOperatorQualifier = queryItem.logicOperatorQualifier;
      }

      // recursion, no .facts no cry
      if (queryItem.structures) {
        handleStructureQuery(queryItem, item, parent);
      }
      if (queryItem.facts) {
        handleFactQuery(queryItem, item, parent);
      }
    };

    // initial recursion call, with parent as result
    processChildren(semanticQuery, result);

    return result;
  }

  _getDefaultQualifier(operator) {
    return get(this._logicOperatorsMap, `${operator}.defaultQualifier`);
  }

  _setFieldsValues(targetFact, sourceQuery) {
    forEach(sourceQuery.fields, (field) => {
      // check if the field exist in current property (license)
      const targetField = find(targetFact.children, { id: field.id });
      if (targetField) {
        extend(targetField, {
          id: field.id,
          value: field.value,
          operation: field.boundOperator,
          validation: field.validation || [],
        });
      }
    });
  }

  _createFactProperty(item, properties) {
    let newItem;
    eachHierarchy(properties, (prop) => {
      if (this._checkFieldId(item) && get(item, 'fields.length') && get(prop, 'children.length')) {
        if (item.fields[0].id === prop.children[0].id) {
          newItem = prop;
        }
      } else if (item.id === prop.id) {
        newItem = prop;
      }
    });

    if (!newItem) {
      return null;
    }

    let result = cloneDeep(newItem);
    result.operator = item.logicOperator || item.defaultOperation || 'AND';

    // RXFE-7999 - set defaultQualifier
    const qualifier = item.logicOperatorQualifier || this._getDefaultQualifier(result.operator);
    if (qualifier) {
      result.operatorQualifier = qualifier;
    }

    result = this._getStructureandAdvancedFacts(result, item);

    // create a new child properties array for future population in next recursion or manually via UI
    result.items = [];
    result.isShell = item.isShell;
    result.isImmutable = item.isImmutable;
    result.exist = !!item.exist;
    result.uuid = uuid();
    return result;
  }

  _getStructureandAdvancedFacts(result, item) {
    const structureandAdvancedResult = result;
    if (result.type === 'structure' && !result.structureUID) {
      structureandAdvancedResult.structureUID = StructureContainers.generateUID('ql');
    }
    if (item.structure && item.structure.length > 0) {
      extend(result, this._setStructureProps(item));
      return result;
    }

    if (item.advancedItems) {
      structureandAdvancedResult.value = item.advancedItems[0].advancedExpression;
      structureandAdvancedResult.databaseId = item.advancedItems[0].databaseId;
    }
    return structureandAdvancedResult;
  }

  _setStructureProps(node) {
    // from import
    return {
      structureData: node.structure,
      structurePreview: node.structurePreview,
      options: node.options || [],
      uuid: uuid(),
      rx1ReactionRole: node.rx1ReactionRole,
    };
  }

  _checkFieldId(item) {
    return FIELD_ID.ECT === item.id || FIELD_ID.PHARM === item.id;
  }
}

export default new QueryParser();
